import React from 'react';
import { useQuery } from '@apollo/react-hooks';
import { Loader } from '@squirrellearningltd/go-teacher-components';
import { READING_STATS_SCHOOL, READING_STATS_CLASS } from './services/graphql';
import styles from './ReadingStats.module.scss';
import { useEffect } from 'react';
import { useRef } from 'react';

const ReadingStats = () => {
    return (
        <>
            <header className={styles.header}>
                <div>
                    <h2 style={{ fontWeight: '900' }}>
                        National Year of Reading 2026
                    </h2>
                    <p>
                        Celebrating the schools and classes across the UK who
                        are leading the way in reading through dedication,
                        enthusiasm, and consistency
                    </p>
                </div>
                <div
                    className={styles.pill}
                    title='When the leaderboard data was last refreshed'
                >
                    <span className={styles.dot}></span>
                    <span>
                        Last updated:{' '}
                        {new Intl.DateTimeFormat('en-GB', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false,
                        })
                            .format(new Date(Date.now()).setHours(0, 0, 0, 0))
                            .replace(',', '')}
                    </span>
                </div>
            </header>
            <div className={styles.readingStatsContainer}>
                <ReadingStatsSchool />
                <ReadingStatsClases />
            </div>
        </>
    );
};

const ReadingStatsSchool = () => {
    const {
        data: schoolReadingStats,
        loading: schoolReadingLoading,
        error: schoolReadingError,
    } = useQuery(READING_STATS_SCHOOL, {
        variables: {
            itemsCounter: 10,
            startFrom: '01/01/2026',
            endOn: '31/12/2026',
        },
    });

    const rowRefs = useRef([]);
    useEffect(() => {
        // attach confetti to first 3 rows only
        const cleanups = rowRefs.current
            .slice(0, 3)
            .map((row, index) => row && attachConfetti(row, index));

        return () => {
            cleanups.forEach((fn) => fn && fn());
        };
    }, [schoolReadingStats]);

    if (schoolReadingLoading) {
        return (
            <div className={styles.loaderContainer}>
                <Loader />
            </div>
        );
    }

    if (schoolReadingError) {
        return (
            <p className={styles.pageText}>
                Data could not be loaded. Please try again later.
            </p>
        );
    }
    return (
        <>
            {schoolReadingStats &&
            schoolReadingStats.readathonOverallStats &&
            schoolReadingStats.readathonOverallStats.length ? (
                <div className={styles.dataContainer}>
                    <div className={styles.dataContainerHeader}>
                        <h5 style={{ fontWeight: '900', marginBottom: '0 ' }}>
                            Top 10 Schools
                        </h5>
                        <div className={styles.meta}>
                            Ranked by average reading logs
                        </div>
                    </div>

                    {schoolReadingStats.readathonOverallStats.map(
                        (stat, index) => {
                            return (
                                <div
                                    key={index}
                                    ref={(el) => (rowRefs.current[index] = el)}
                                    className={`${styles.dataRow} ${
                                        [0, 1, 2].includes(index)
                                            ? styles.podium
                                            : ''
                                    } ${
                                        index === 0
                                            ? styles.firstPlace
                                            : index === 1
                                            ? styles.secondPlace
                                            : index === 2
                                            ? styles.thirdPlace
                                            : ''
                                    } `}
                                >
                                    {index === 0 && (
                                        <svg
                                            className={styles.crown}
                                            viewBox='0 0 24 24'
                                            aria-hidden='true'
                                        >
                                            <path
                                                fill='rgba(214,180,76,1)'
                                                d='M3 7l4.5 4L12 5l4.5 6L21 7v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z'
                                            ></path>
                                            <path
                                                fill='rgba(255,255,255,.86)'
                                                d='M7 19h10a1 1 0 0 0 1-1v-2H6v2a1 1 0 0 0 1 1z'
                                            ></path>
                                        </svg>
                                    )}
                                    {index < 3 && (
                                        <canvas
                                            className={styles.confetti}
                                            aria-hidden='true'
                                        ></canvas>
                                    )}
                                    <div
                                        className={`${styles.dataRowRankBox} ${
                                            index === 0
                                                ? styles.firstPlace
                                                : index === 1
                                                ? styles.secondPlace
                                                : index === 2
                                                ? styles.thirdPlace
                                                : ''
                                        }`}
                                    >
                                        {index + 1}
                                    </div>
                                    <div className={styles.dataRowMain}>
                                        {stat.school.avatarUrl ? (
                                            <img
                                                className={
                                                    styles.dataRowMainLogoOfSchool
                                                }
                                                src={stat.school.avatarUrl}
                                            />
                                        ) : (
                                            <div
                                                className={
                                                    styles.dataRowMainLogo
                                                }
                                            ></div>
                                        )}

                                        <div className={styles.dataRowMainName}>
                                            {stat.schoolName}
                                        </div>
                                    </div>
                                    <div className={styles.dataRowScore}>
                                        <div
                                            className={
                                                styles.dataRowScoreNumber
                                            }
                                        >
                                            {stat.score}
                                        </div>
                                        <div
                                            className={styles.dataRowScoreLabel}
                                        >
                                            avg logs
                                        </div>
                                    </div>
                                </div>
                            );
                        }
                    )}
                </div>
            ) : (
                <p>No data available</p>
            )}
        </>
    );
};

const ReadingStatsClases = () => {
    const {
        data: classReadingStats,
        loading: classReadingLoading,
        error: classReadingError,
    } = useQuery(READING_STATS_CLASS, {
        variables: {
            itemsCounter: 10,
            startFrom: '01/01/2026',
            endOn: '31/12/2026',
        },
    });

    const rowRefs = useRef([]);
    useEffect(() => {
        // attach confetti to first 3 rows only
        const cleanups = rowRefs.current
            .slice(0, 3)
            .map((row, index) => row && attachConfetti(row, index));

        return () => {
            cleanups.forEach((fn) => fn && fn());
        };
    }, [classReadingStats]);

    if (classReadingLoading) {
        return (
            <div className={styles.loaderContainer}>
                <Loader />
            </div>
        );
    }

    if (classReadingError) {
        return (
            <p className={styles.pageText}>
                Data could not be loaded. Please try again later.
            </p>
        );
    }

    return (
        <>
            {classReadingStats &&
            classReadingStats.readathonStudentsClassOverallStats &&
            classReadingStats.readathonStudentsClassOverallStats.length ? (
                <div className={styles.dataContainer}>
                    <div className={styles.dataContainerHeader}>
                        <h5 style={{ fontWeight: '900', marginBottom: '0 ' }}>
                            Top 10 Classes
                        </h5>
                        <div className={styles.meta}>
                            Ranked by average reading logs
                        </div>
                    </div>

                    {classReadingStats.readathonStudentsClassOverallStats.map(
                        (stat, index) => {
                            return (
                                <div
                                    key={index}
                                    ref={(el) => (rowRefs.current[index] = el)}
                                    className={`${styles.dataRow} ${
                                        [0, 1, 2].includes(index)
                                            ? styles.podium
                                            : ''
                                    } ${
                                        index === 0
                                            ? styles.firstPlace + ''
                                            : index === 1
                                            ? styles.secondPlace
                                            : index === 2
                                            ? styles.thirdPlace
                                            : ''
                                    } `}
                                >
                                    {index === 0 && (
                                        <svg
                                            className={styles.crown}
                                            viewBox='0 0 24 24'
                                            aria-hidden='true'
                                        >
                                            <path
                                                fill='rgba(214,180,76,1)'
                                                d='M3 7l4.5 4L12 5l4.5 6L21 7v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z'
                                            ></path>
                                            <path
                                                fill='rgba(255,255,255,.86)'
                                                d='M7 19h10a1 1 0 0 0 1-1v-2H6v2a1 1 0 0 0 1 1z'
                                            ></path>
                                        </svg>
                                    )}
                                    {index < 3 && (
                                        <canvas
                                            className={styles.confetti}
                                            aria-hidden='true'
                                        ></canvas>
                                    )}
                                    <div
                                        className={`${styles.dataRowRankBox} ${
                                            index === 0
                                                ? styles.firstPlace
                                                : index === 1
                                                ? styles.secondPlace
                                                : index === 2
                                                ? styles.thirdPlace
                                                : ''
                                        }`}
                                    >
                                        {index + 1}
                                    </div>
                                    <div className={styles.dataRowMain}>
                                        <div className={styles.dataRowMainName}>
                                            <div>{stat.studentsClassName}</div>
                                            <div
                                                style={{
                                                    fontSize: '12px',
                                                    fontWeight: 'normal',
                                                }}
                                            >
                                                {stat.studentsClass.school.name}
                                            </div>
                                        </div>
                                    </div>
                                    <div className={styles.dataRowScore}>
                                        <div
                                            className={
                                                styles.dataRowScoreNumber
                                            }
                                        >
                                            {stat.totalLogs}
                                        </div>
                                        <div
                                            className={styles.dataRowScoreLabel}
                                        >
                                            avg logs
                                        </div>
                                    </div>
                                </div>
                            );
                        }
                    )}
                </div>
            ) : (
                <p>No data available</p>
            )}
        </>
    );
};

export default ReadingStats;

function attachConfetti(row, index) {
    const canvas = row.querySelector('canvas');
    const ctx = canvas.getContext('2d');
    let particles = [];
    let raf = null;
    let running = false;

    function resize() {
        const rect = row.getBoundingClientRect();
        canvas.width = Math.max(1, Math.floor(rect.width * devicePixelRatio));
        canvas.height = Math.max(1, Math.floor(rect.height * devicePixelRatio));
        ctx.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);
    }

    function burst() {
        resize();
        const rect = row.getBoundingClientRect();
        const rank = Number(index);

        const palettes = {
            0: ['#D6B44C', '#7D9826', '#93B13A', '#3BB2A0', '#FFFFFF'],
            1: ['#B9C0C8', '#7D9826', '#3BB2A0', '#7C5CFF', '#FFFFFF'],
            2: ['#C68A57', '#7D9826', '#93B13A', '#FF5FA2', '#FFFFFF'],
        };
        const colors = palettes[rank] || [
            '#7D9826',
            '#93B13A',
            '#3BB2A0',
            '#7C5CFF',
        ];

        const count = 68;
        particles = Array.from({ length: count }).map(() => ({
            x: rect.width * (0.2 + Math.random() * 0.6),
            y: rect.height * (0.15 + Math.random() * 0.2),
            vx: (Math.random() * 2 - 1) * (1.7 + Math.random() * 2.0),
            vy: -(2.0 + Math.random() * 3.0),
            g: 0.14 + Math.random() * 0.06,
            r: 2 + Math.random() * 3,
            a: 1,
            rot: Math.random() * Math.PI,
            vr: (Math.random() * 2 - 1) * 0.22,
            color: colors[Math.floor(Math.random() * colors.length)],
            life: 56 + Math.random() * 34,
        }));
    }

    function frame() {
        if (!running) return;
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        for (const p of particles) {
            p.x += p.vx;
            p.y += p.vy;
            p.vy += p.g;
            p.rot += p.vr;
            p.life -= 1;
            p.a = Math.max(0, Math.min(1, p.life / 88));

            ctx.save();
            ctx.globalAlpha = p.a;
            ctx.translate(p.x, p.y);
            ctx.rotate(p.rot);
            ctx.fillStyle = p.color;
            ctx.fillRect(-p.r, -p.r * 0.6, p.r * 2.3, p.r * 1.2);
            ctx.restore();
        }

        particles = particles.filter(
            (p) => p.life > 0 && p.y < canvas.height / devicePixelRatio + 40
        );
        if (particles.length === 0) {
            stop();
            return;
        }
        raf = requestAnimationFrame(frame);
    }

    function start() {
        if (running) return;
        running = true;
        row.classList.add('confetti-on');
        burst();
        frame();
    }
    function stop() {
        running = false;
        row.classList.remove('confetti-on');
        if (raf) cancelAnimationFrame(raf);
        raf = null;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
    }

    row.addEventListener('mouseenter', start);
    row.addEventListener('mouseleave', stop);
    row.addEventListener('focus', start);
    row.addEventListener('blur', stop);
    row.addEventListener('click', () => (running ? stop() : start()));

    return () => {
        stop();
        row.removeEventListener('mouseenter', start);
        row.removeEventListener('mouseleave', stop);
        row.removeEventListener('focus', start);
        row.removeEventListener('blur', stop);
    };
}
